import get from 'get-object-path';
import {
    isAthleteSelected,
    isCrewSelected,
    sessionHasResults,
    sessionHasHrData,
    sessionHasGpsData
} from '@/helpers/sessionResultsHelper';
import { LUDUM_USER_ROLES } from '@/constants';

export const sessionResults = {
    namespaced: true,

    state: {
        session: null,
        athletesHrFiles: [],
        athletesGpsFiles: [],
        crewsGpsFiles: []
    },

    getters: {
        session(state) {
            return state.session;
        },

        sessionName(state) {
            return get(state, 'session.name');
        },

        sportId(state) {
            return get(state, 'session.sport.id');
        },

        canAddScores(state) {
            return get(state, 'session.user_actions.data.add_all_scores');
        },

        isSeatRacing(state) {
            return get(state, 'session.seat_racing');
        },

        isCompetitive(state) {
            return get(state, 'session.competitive');
        },

        piecesCount(state) {
            return get(state, 'session.session.data.structure.data[0].pieces_count');
        },

        pieces(state) {
            return get(state, 'session.session.data.structure.data[0].pieces.data') || [];
        },

        hasTimePiece(state, getters) {
            return getters.pieces.some(piece => piece.planned_distance);
        },

        hasDistancePiece(state, getters) {
            return getters.pieces.some(piece => piece.planned_time);
        },

        hasSessionResults(state) {
            return (
                sessionHasResults(state.session) || sessionHasHrData(state.session) || sessionHasGpsData(state.session)
            );
        },

        crewsSessionFiles(state, getters, rootState, rootGetters) {
            const files = get(state, 'session.session_files.data.crews.data') || [];
            const filterCrews = rootGetters['sessionResultsFilter/selectedCrews'];
            return files.filter(({ id }) => isCrewSelected(filterCrews, id));
        },

        crewsSessionFilesGps(state, getters) {
            return getters.crewsSessionFiles.filter(crew => get(crew, 'session_crew_files.data.gps_file'));
        },

        alternatives(state) {
            const alternatives = get(state, 'session.alternative_results.data') || [];
            return _.sortBy(alternatives, 'full_name');
        },

        sportUserResultsUsers(state) {
            return get(state, 'session.sport_user_results.data[0].users.data') || [];
        },

        sessionFilesUsers(state) {
            const users = get(state, 'session.session_files.data.users.data') || [];
            return _.sortBy(users, 'full_name');
        },

        sportCrewResultsCrews(state) {
            return get(state, 'session.sport_crew_results.data[0].crews.data') || [];
        },

        participants(state) {
            const participants = get(state, 'session.participants.data') || [];
            return _.sortBy(participants, 'user.data.full_name');
        },

        attendanceRoleId(state) {
            return get(state, 'session.attendance.data.user_role_id');
        },

        userIsAthlete(state, getters) {
            return getters.attendanceRoleId === LUDUM_USER_ROLES.ATHLETE;
        },

        userIsCoach(state, getters) {
            return [LUDUM_USER_ROLES.COACH, LUDUM_USER_ROLES.HEAD_COACH].includes(getters.attendanceRoleId);
        },

        athletesHrFiles(state, getters, rootState, rootGetters) {
            const filterAthletes = rootGetters['sessionResultsFilter/selectedAthletes'];
            const athletes = state.athletesHrFiles.filter(({ id }) => isAthleteSelected(filterAthletes, id));
            return _.sortBy(athletes, 'full_name');
        },

        athletesGpsFiles(state, getters, rootState, rootGetters) {
            const filterAthletes = rootGetters['sessionResultsFilter/selectedAthletes'];
            const athletes = state.athletesGpsFiles.filter(({ id }) => isAthleteSelected(filterAthletes, id));
            return _.sortBy(athletes, 'full_name');
        },

        crewsGpsFiles(state, getters, rootState, rootGetters) {
            const filterCrews = rootGetters['sessionResultsFilter/selectedCrews'];
            const crews = state.crewsGpsFiles.filter(({ id }) => isCrewSelected(filterCrews, id));
            return _.sortBy(crews, 'name');
        }
    },

    mutations: {
        resetState(state) {
            state.session = null;
            state.athletesHrFiles = [];
            state.athletesGpsFiles = [];
            state.crewsGpsFiles = [];
        },

        fillSession(state, payload) {
            state.session = Object.freeze(payload);
        },

        fillAthletesHrFiles(state, hrFile) {
            state.athletesHrFiles.push(hrFile);
        },

        fillAthletesGpsFiles(state, gpsFile) {
            state.athletesGpsFiles.push(gpsFile);
        },

        fillCrewsGpsFiles(state, gpsFile) {
            state.crewsGpsFiles.push(gpsFile);
        }
    }
};
